// Google Analytics is only loaded after the visitor agrees (cookie banner).
// Nothing is requested from Google and no cookie is set before that.

const MEASUREMENT_ID = "G-YBZZGHJ8WC";
const STORAGE_KEY = "mollton-analytics-consent";
export const OPEN_SETTINGS_EVENT = "mollton-open-cookie-settings";

export type Consent = "granted" | "denied";

declare global {
  interface Window {
    dataLayer?: unknown[];
    [key: `ga-disable-${string}`]: boolean | undefined;
  }
}

let loaded = false;

export function getConsent(): Consent | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    return null; // storage blocked: ask again, track nothing
  }
}

// gtag.js expects the `arguments` object itself in the dataLayer, not an array
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function track(..._args: unknown[]) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
}

function loadAnalytics() {
  if (loaded) return;
  loaded = true;
  window[`ga-disable-${MEASUREMENT_ID}`] = false;
  track("js", new Date());
  // page views are sent by hand, so route changes in the site are counted too
  track("config", MEASUREMENT_ID, { send_page_view: false, anonymize_ip: true });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);
  document.addEventListener("click", trackLinkClick);
  window.addEventListener("scroll", trackScrollDepth, { passive: true });
  trackPageView();
}

function trackEvent(name: string, params: Record<string, unknown>) {
  if (!loaded || getConsent() !== "granted") return;
  track("event", name, params);
}

// One listener for all links, so the contact links that are only built in the
// browser (see contactSection.component) are covered too. The address and the
// phone number are never sent, only the kind of link.
function trackLinkClick(event: MouseEvent) {
  const link = (event.target as Element | null)?.closest?.("a");
  if (!link) return;
  const page_path = window.location.pathname;
  const host = link.hostname.replace(/^www\./, "");
  const method =
    link.protocol === "mailto:"
      ? "email"
      : link.protocol === "tel:"
        ? "phone"
        : host === "facebook.com" || host.endsWith(".facebook.com")
          ? "facebook"
          : host === "instagram.com" || host.endsWith(".instagram.com")
            ? "instagram"
            : null;
  if (method) {
    trackEvent("contact_click", { method, page_path });
  } else if (link.origin === window.location.origin && link.closest("header")) {
    trackEvent("nav_click", {
      link_text: link.textContent?.trim() || "Logo",
      link_url: link.pathname,
      page_path,
    });
  }
}

const SCROLL_STEPS = [25, 50, 75, 100];
let reachedSteps = new Set<number>();

// Each milestone is sent once per page view; trackPageView starts over
function trackScrollDepth() {
  const root = document.documentElement;
  const percent = ((window.scrollY + window.innerHeight) / root.scrollHeight) * 100;
  SCROLL_STEPS.forEach((step) => {
    // 99.5: pixel rounding can keep the very bottom just below 100
    if (percent >= (step === 100 ? 99.5 : step) && !reachedSteps.has(step)) {
      reachedSteps.add(step);
      trackEvent("scroll_depth", { percent: step, page_path: window.location.pathname });
    }
  });
}

function stopAnalytics() {
  window[`ga-disable-${MEASUREMENT_ID}`] = true;
  // remove the cookies Google Analytics already set (_ga, _ga_<id>)
  const host = window.location.hostname;
  const domains = [host, `.${host}`, `.${host.replace(/^www\./, "")}`];
  document.cookie
    .split(";")
    .map((cookie) => cookie.split("=")[0].trim())
    .filter((name) => name === "_ga" || name.startsWith("_ga_") || name === "_gid")
    .forEach((name) =>
      domains.forEach((domain) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`;
      })
    );
}

export function setConsent(value: Consent) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // choice is only kept for this page view
  }
  if (value === "granted") loadAnalytics();
  else stopAnalytics();
}

/** Loads Analytics at startup if the visitor agreed earlier */
export function initAnalytics() {
  if (getConsent() === "granted") loadAnalytics();
}

export function trackPageView() {
  if (!loaded || getConsent() !== "granted") return;
  reachedSteps = new Set();
  track("event", "page_view", {
    page_path: window.location.pathname + window.location.search,
    page_location: window.location.href,
    page_title: document.title,
  });
}

export function openCookieSettings() {
  window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));
}
